import { Stack, Typography } from "@mui/material";
import {
  Button,
  LoadingIndicator,
  useCornerRadius,
  useFoundationColorTokens,
  useShadow,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { EmailCampaignOptOutForm } from "../../components/forms/EmailCampaignOptOutForm";
import { useTranslation } from "react-i18next";
import {
  useOptOutOfCampaignStore,
  useOptOutScreenDetailsStore,
} from "../../stores/Hooks";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import { useSearchParams } from "react-router-dom";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { GetOptOutScreenDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getOptOutScreenDetailsRPC";
import { OptOutOfCampaignRPC } from "@pulse/pulse-rpcs/lib/email-cycle/optOutOfCampaignRPC";

type ContentToShow = "FORM" | "SUCCESS_MESSAGE";

const OPT_OUT_FORM_WIDTH = "560px";

export const EmailCampaignOptOutPageContent: React.FC = observer(() => {
  const [searchParams] = useSearchParams();
  const [contentToShow, setContentToShow] = useState<ContentToShow>("FORM");

  const { t } = useTranslation();
  const navigateTo = useRouteNavigator();
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const cornerRadius = useCornerRadius();
  const shadow = useShadow();
  const typography = useTypography();

  const optOutScreenDetailsStore = useOptOutScreenDetailsStore();
  const optOutOfCampaignStore = useOptOutOfCampaignStore();

  const emailId = searchParams.get(RouteSearchParam.EmailId);

  useEffect(() => {
    if (!emailId) return navigateTo.pageNotFound();

    optOutScreenDetailsStore
      .getOptOutScreenDetails(emailId)
      .catch(navigateTo.internalServerError);
  }, []);

  useEffect(() => {
    if (optOutScreenDetailsStore.getOptOutScreenDetailsRPCError) {
      switch (optOutScreenDetailsStore.getOptOutScreenDetailsRPCError) {
        case GetOptOutScreenDetailsRPC.RPCError.InvalidEmailId:
          return navigateTo.pageNotFound();
        default:
          optOutScreenDetailsStore.getOptOutScreenDetailsRPCError satisfies never;
      }
    }
  }, []);

  const handleSubmitOptOutForm = async (): Promise<void> => {
    if (!emailId) return navigateTo.pageNotFound();

    await optOutOfCampaignStore
      .getOptOutOfCampaign(emailId)
      .catch(navigateTo.internalServerError);

    switch (optOutOfCampaignStore.optOutOfCampaignRPCError) {
      case null:
        setContentToShow("SUCCESS_MESSAGE");
        break;
      case OptOutOfCampaignRPC.RPCError.InvalidEmailId:
        navigateTo.pageNotFound();
        break;
      default:
        optOutOfCampaignStore.optOutOfCampaignRPCError satisfies never;
    }
  };

  if (optOutScreenDetailsStore.loadingOptOutScreenDetails) {
    return <LoadingIndicator variant="overlay" isLoading />;
  }

  if (contentToShow === "SUCCESS_MESSAGE") {
    return (
      <SuccessDialog
        title={t("emailCycles.optOutFormHeader", {
          projectName: optOutScreenDetailsStore.projectName.name,
        })}
        message={t("emailCycles.optOutOfCampaignSuccessMessage")}
      />
    );
  }

  if (contentToShow === "FORM") {
    return (
      <>
        <Stack flex={1} alignItems="center" justifyContent="center">
          <Stack
            bgcolor={tokens.backgroundElevatedLevel1}
            borderRadius={cornerRadius.radiusXS}
            boxShadow={shadow.level3}
            divider={<DividerComponent orientation="horizontal" width="100%" />}
            width={OPT_OUT_FORM_WIDTH}
          >
            {/* Header */}
            <Typography padding={spacing.spaceLG} {...typography.sh3}>
              {t("emailCycles.optOutFormHeader", {
                projectName: optOutScreenDetailsStore.projectName.name,
              })}
            </Typography>

            <EmailCampaignOptOutForm />

            {/* Footer */}
            <Stack
              direction="row"
              justifyContent="end"
              padding={spacing.spaceLG}
            >
              <Button
                label={t("common.submit")}
                name="optOutFormSubmitButton"
                onClick={handleSubmitOptOutForm}
                size="small"
                variant="filled"
              />
            </Stack>
          </Stack>
        </Stack>

        <LoadingIndicator
          variant="overlay"
          isLoading={optOutOfCampaignStore.loadingOptOutOfCampaign}
        />
      </>
    );
  }
});
